'use client';

/**
 * "What's this?" popover: a small (i) icon next to a label that opens one
 * or two sentences of plain-English explanation plus a "Read more" link
 * into the Gift Aid help page.
 *
 * The body has to be self-contained: for many trustees it is their first
 * encounter with a term like GASDS or aggregation, so answer the question
 * right there and let the link be the optional deep dive. Works by tap on
 * phones (Radix Popover), so nothing is hidden on small screens.
 */

import Link from 'next/link';
import { HelpCircle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface InfoPopoverProps {
  body: string;
  /** Anchor on /dashboard/help/gift-aid, including the leading '#'. */
  helpAnchor: string;
  /** Accessible name for the trigger, e.g. "What is a sponsored event?" */
  label: string;
  className?: string;
}

export function InfoPopover({ body, helpAnchor, label, className }: InfoPopoverProps) {
  return (
    <Popover>
      <PopoverTrigger
        type="button"
        aria-label={label}
        // Stop the click reaching a surrounding radio row or label so the
        // (i) never changes a selection. Padding gives a 24px tap target
        // without moving the icon.
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'inline-flex -m-1 rounded p-1 align-middle text-gray-400 hover:text-gray-600 focus:text-gray-600 focus:outline-hidden',
          className,
        )}
      >
        <HelpCircle className="h-4 w-4" />
      </PopoverTrigger>
      <PopoverContent onClick={(e) => e.stopPropagation()}>
        <p className="mb-3 leading-relaxed">{body}</p>
        <Link
          href={`/dashboard/help/gift-aid${helpAnchor}`}
          className="text-sm font-medium text-primary hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Read more →
        </Link>
      </PopoverContent>
    </Popover>
  );
}
